import type { NotificationPlan } from '@autoday/core';
import type { ChannelStatus, ScheduleReport } from './types.ts';
import type { ServerNotificationStatus } from './webPush.ts';
import type { NotificationTestRecord } from '../store/storage.ts';
import type { SyncSnapshot } from '../sync/syncClient.ts';
import { tr } from '../i18n/i18n.ts';

/**
 * Påminnelsekoll (Reminder Guardian): en ärlig sammanfattning av vad som går att kontrollera härifrån.
 * Varje dimension bedöms för sig och redovisas med källa och tid; ingen tillförlitlighetsprocent, ingen
 * leveransgaranti och ingen påstådd bakgrundsövervakning.
 *
 *  - Behörighet: operativsystemets/webbläsarens notisbehörighet just nu.
 *  - Kanal: kan den här profilen och kanalen visa påminnelser när appen är stängd?
 *  - Planering: vad AutoDay har planerat (lokalt eller hos servern) och om enheten bekräftat listan.
 *  - Synk: har servern de senaste ändringarna? (Bara konto; servern planerar från synkad data.)
 *  - Leverantör: har push-tjänsten tagit emot utskick, fungerar workern? Accepterad ≠ visad.
 *  - Enhetens rapport: service workern rapporterade att en testnotis visades.
 *  - Din bekräftelse: du såg testnotisen. Gäller bara samma kanal och samma behörighet, och åldras.
 * Ett gammalt lyckat test övertrumfar aldrig en ändrad behörighet eller en trasig kanal: varje risk
 * i någon dimension ger rött oavsett test.
 */
export type CheckState = 'ok' | 'attention' | 'risk' | 'unknown' | 'na';
export type GuardianAction = 'request-permission' | 'open-app-settings' | 'enable-exact-alarms' | 'subscribe-push' | 'install-pwa' | 'connect-account' | 'run-test' | 'sync-now' | 'sign-in-again' | 'recheck';

export interface GuardianCheck {
  id: 'permission' | 'channel' | 'planning' | 'sync' | 'provider' | 'device' | 'manual';
  title: string;
  state: CheckState;
  detail: string;
  /** Tidpunkt för observationen (om någon). */
  observedAt: string | null;
  action?: GuardianAction;
}

export interface GuardianInput {
  now: string;
  online: boolean;
  profile: 'guest' | 'account';
  /** Kontosessionens tillstånd (gäst = 'guest'). */
  session: 'guest' | 'checking' | 'signed-in' | 'offline' | 'expired';
  status: ChannelStatus | null;
  report: ScheduleReport | null;
  plan: NotificationPlan | null;
  sync: Pick<SyncSnapshot, 'state' | 'pending' | 'lastSyncAt' | 'conflicts'>;
  server: ServerNotificationStatus | null;
  serverError: string | null;
  serverCheckedAt: string | null;
  test: NotificationTestRecord | null;
}

export interface GuardianResult {
  /** ok = allt som går att kontrollera är uppfyllt och bekräftat; limited = fungerar bara med appen öppen. */
  overall: 'ok' | 'attention' | 'risk' | 'limited' | 'unknown';
  headline: string;
  summary: string;
  checks: GuardianCheck[];
  /** Nästa handling, i prioritetsordning. */
  actions: GuardianAction[];
  lastCheckedAt: string | null;
  /** Senaste kontroll är äldre än STALE_MINUTES. */
  stale: boolean;
}

export const STALE_MINUTES = 360;
/** Ett manuellt test räknas som färskt i 30 dagar; därefter föreslås ett nytt. */
export const TEST_FRESH_DAYS = 30;

const minutesBetween = (a: string, b: string) => (Date.parse(b) - Date.parse(a)) / 60_000;

export function assessReminders(i: GuardianInput): GuardianResult {
  const checks: GuardianCheck[] = [];
  const st = i.status;
  const lastCheckedAt = [i.report?.at ?? null, i.serverCheckedAt].filter((x): x is string => !!x).sort().at(-1) ?? null;
  const stale = !lastCheckedAt || minutesBetween(lastCheckedAt, i.now) > STALE_MINUTES;
  const guest = i.profile === 'guest';
  const usesServer = st?.channel === 'web-push';

  if (!st) {
    return {
      overall: 'unknown',
      headline: tr('Kontrollerar påminnelser…', 'Checking reminders…'),
      summary: tr('Inga observationer ännu.', 'No observations yet.'),
      checks: [],
      actions: ['recheck'],
      lastCheckedAt,
      stale,
    };
  }

  // 1. Behörighet. Utan bakgrundskanal (gäst i webbläsaren) gäller den bara skrivbordsnotiser medan appen
  // är öppen – påminnelserna i appen visas ändå, så nekad behörighet är där ingen risk.
  const perm = st.permission;
  checks.push({
    id: 'permission',
    title: tr('Behörighet', 'Permission'),
    state: perm === 'granted' ? 'ok' : perm === 'prompt' || st.channel === 'none' ? 'attention' : 'risk',
    detail:
      st.channel === 'none' && perm !== 'granted'
        ? tr('Notiser är inte tillåtna. I gästläget påverkar det bara skrivbordsnotiser medan AutoDay är öppen; påminnelserna visas ändå i appen.', 'Notifications are not allowed. In guest mode this only affects desktop notifications while AutoDay is open; reminders still show in the app.')
        : perm === 'granted'
        ? tr('Notiser är tillåtna för AutoDay på den här enheten.', 'Notifications are allowed for AutoDay on this device.')
        : perm === 'denied'
          ? tr('Notiser är nekade. Ändra i webbläsarens eller telefonens inställningar.', 'Notifications are blocked. Change this in the browser or phone settings.')
          : perm === 'prompt'
            ? tr('Du har inte tillåtit notiser ännu.', 'You have not allowed notifications yet.')
            : tr('Den här webbläsaren eller enheten stöder inte notiser.', 'This browser or device does not support notifications.'),
    observedAt: i.report?.at ?? null,
    ...(perm === 'prompt' ? { action: 'request-permission' as const } : perm === 'denied' ? { action: 'open-app-settings' as const } : {}),
  });

  // 2. Kanalens bakgrundsstöd
  const exactMissing = st.exactAlarms === 'denied';
  checks.push({
    id: 'channel',
    title: tr('När appen är stängd', 'When the app is closed'),
    state: st.channel === 'none' ? 'attention' : st.backgroundCapable ? (exactMissing ? 'attention' : 'ok') : 'risk',
    detail:
      st.channel === 'none'
        ? tr('Den här profilen saknar bakgrundskanal: påminnelser visas bara medan AutoDay är öppen. Logga in för Web Push, eller använd Android-appen.', 'This profile has no background channel: reminders only show while AutoDay is open. Sign in for Web Push, or use the Android app.')
        : st.backgroundCapable
          ? exactMissing
            ? tr(`${st.label}. Exakta larm är inte tillåtna – påminnelser kan komma flera minuter sent.`, `${st.label}. Exact alarms are not allowed – reminders may arrive several minutes late.`)
            : tr(`${st.label}. Kanalen kan visa påminnelser när appen är stängd.`, `${st.label}. The channel can show reminders when the app is closed.`)
          : usesServer && perm === 'granted'
            ? tr('Push är inte aktiverat på den här enheten.', 'Push is not turned on on this device.')
            : tr(`${st.label}. Kanalen kan inte leverera när appen är stängd just nu.`, `${st.label}. The channel cannot deliver while the app is closed right now.`),
    observedAt: i.report?.at ?? null,
    ...(st.channel === 'none' ? { action: 'connect-account' as const } : !st.backgroundCapable && usesServer && perm === 'granted' ? { action: 'subscribe-push' as const } : exactMissing ? { action: 'enable-exact-alarms' as const } : {}),
  });

  // 3. Planering
  const plan = i.plan;
  const report = i.report;
  const points = plan?.totalPointsInHorizon ?? 0;
  let planning: GuardianCheck;
  if (!plan || !report) {
    planning = { id: 'planning', title: tr('Planering', 'Planning'), state: 'unknown', detail: tr('Ingen planering registrerad ännu.', 'No planning recorded yet.'), observedAt: null, action: 'recheck' };
  } else if (report.channel === 'android-local' || report.channel === 'ios-local') {
    const missing = report.verifiedPending !== null && report.verifiedPending < report.requested;
    planning = {
      id: 'planning',
      title: tr('Planering på enheten', 'Planning on this device'),
      state: report.errors.length || missing ? 'risk' : report.overflowPoints > 0 ? 'attention' : 'ok',
      detail: missing
        ? tr(`${report.verifiedPending} av ${report.requested} notiser finns i systemets lista.`, `${report.verifiedPending} of ${report.requested} notifications are in the system list.`)
        : report.requested === 0
          ? tr('Inga kommande påminnelser att schemalägga.', 'No upcoming reminders to schedule.')
          : tr(`${report.requested} notiser schemalagda och bekräftade i systemets lista${report.overflowPoints ? `; ${report.overflowPoints} senare fylls på när appen öppnas` : ''}.`, `${report.requested} notifications scheduled and confirmed in the system list${report.overflowPoints ? `; ${report.overflowPoints} later ones are added when the app is opened` : ''}.`),
      observedAt: report.at,
      ...(report.errors.length || missing ? { action: 'recheck' as const } : {}),
    };
  } else if (usesServer) {
    planning = {
      id: 'planning',
      title: tr('Planering hos servern', 'Planning on the server'),
      state: 'ok',
      detail: points
        ? tr(`${points} larmpunkter de närmaste 60 dagarna. Servern köar utskick${i.server?.serverHorizonHours ? ` ${i.server.serverHorizonHours} h i förväg` : ''}.`, `${points} alarm points in the next 60 days. The server queues sends${i.server?.serverHorizonHours ? ` ${i.server.serverHorizonHours} h ahead` : ''}.`)
        : tr('Inga kommande påminnelser de närmaste 60 dagarna.', 'No upcoming reminders in the next 60 days.'),
      observedAt: report.at,
    };
  } else {
    planning = {
      id: 'planning',
      title: tr('Planering', 'Planning'),
      state: 'na',
      detail: points ? tr(`${points} larmpunkter de närmaste 60 dagarna visas i appen när de inträffar, om AutoDay är öppen.`, `${points} alarm points in the next 60 days are shown in the app when they occur, if AutoDay is open.`) : tr('Inga kommande påminnelser.', 'No upcoming reminders.'),
      observedAt: report.at,
    };
  }
  checks.push(planning);

  // 4. Synk (servern planerar från synkad data)
  if (guest) {
    checks.push({ id: 'sync', title: tr('Synk', 'Sync'), state: 'na', detail: tr('Gäller inte: inget konto, allt finns på den här enheten.', 'Not applicable: no account, everything is on this device.'), observedAt: null });
  } else {
    const s = i.sync;
    const state: CheckState = i.session === 'expired' || s.state === 'auth-required' ? 'risk' : s.conflicts.length || s.state === 'error' ? 'risk' : s.state === 'offline' || !i.online || s.pending > 0 ? 'attention' : s.state === 'idle' && s.lastSyncAt ? 'ok' : 'unknown';
    checks.push({
      id: 'sync',
      title: tr('Synk med servern', 'Sync with the server'),
      state,
      detail:
        i.session === 'expired' || s.state === 'auth-required'
          ? tr('Sessionen har gått ut. Servern får inga nya ändringar förrän du loggar in igen.', 'The session has expired. The server gets no new changes until you sign in again.')
          : s.conflicts.length
            ? tr('Det finns synkkonflikter. Påminnelser för de posterna kan bygga på en äldre version.', 'There are sync conflicts. Reminders for those items may be based on an older version.')
            : !i.online || s.state === 'offline'
              ? tr(`Offline. ${s.pending ? `${s.pending} ändringar har inte nått servern; ` : ''}serverns påminnelser bygger på senast synkade data.`, `Offline. ${s.pending ? `${s.pending} changes have not reached the server; ` : ''}the server’s reminders are based on the last synced data.`)
              : s.pending
                ? tr(`${s.pending} ändringar väntar på synk. Servern planerar dem när de kommit fram.`, `${s.pending} changes are waiting to sync. The server plans them once they arrive.`)
                : s.lastSyncAt
                  ? tr('Servern har dina senaste ändringar.', 'The server has your latest changes.')
                  : tr('Ingen bekräftad synk ännu.', 'No confirmed sync yet.'),
      observedAt: s.lastSyncAt,
      ...(i.session === 'expired' || s.state === 'auth-required' ? { action: 'sign-in-again' as const } : s.pending || s.state === 'error' ? { action: 'sync-now' as const } : {}),
    });
  }

  // 5. Leverantör / server (Web Push och fjärrpush)
  if (!usesServer) {
    checks.push({
      id: 'provider',
      title: tr('Push-tjänst', 'Push service'),
      state: 'na',
      detail: st.channel === 'none' ? tr('Gäller inte: ingen server används i gästläget.', 'Not applicable: no server is used in guest mode.') : tr('Gäller inte: telefonen schemalägger påminnelserna själv.', 'Not applicable: the phone schedules the reminders itself.'),
      observedAt: null,
    });
  } else if (!i.online) {
    checks.push({ id: 'provider', title: tr('Server och push-tjänst', 'Server and push service'), state: 'unknown', detail: tr('Kan inte kontrolleras offline.', 'Cannot be checked while offline.'), observedAt: i.serverCheckedAt });
  } else if (i.serverError) {
    checks.push({ id: 'provider', title: tr('Server och push-tjänst', 'Server and push service'), state: 'unknown', detail: tr(`Servern svarade inte: ${i.serverError}`, `The server did not respond: ${i.serverError}`), observedAt: i.serverCheckedAt, action: 'recheck' });
  } else if (i.server) {
    const sv = i.server;
    const bad = !sv.pushConfigured || sv.workerStale || sv.deviceSubscribed === false;
    checks.push({
      id: 'provider',
      title: tr('Server och push-tjänst', 'Server and push service'),
      state: bad ? 'risk' : sv.recentErrors.length ? 'attention' : 'ok',
      detail: !sv.pushConfigured
        ? tr('Servern saknar push-nycklar och kan inte skicka.', 'The server has no push keys and cannot send.')
        : sv.workerStale
          ? tr('Notisworkern rapporterar inte – påminnelser skickas inte just nu.', 'The notification worker is not reporting – reminders are not being sent right now.')
          : sv.deviceSubscribed === false
            ? tr('Servern har ingen push-prenumeration för den här enheten.', 'The server has no push subscription for this device.')
            : sv.lastAcceptedAt
              ? tr(`Push-tjänsten tog senast emot ett utskick ${sv.lastAcceptedAt.slice(0, 16).replace('T', ' ')} (UTC). Mottaget betyder inte visat.`, `The push service last accepted a send at ${sv.lastAcceptedAt.slice(0, 16).replace('T', ' ')} (UTC). Accepted does not mean shown.`)
              : tr('Servern och workern svarar. Inget utskick har accepterats ännu.', 'The server and worker respond. No send has been accepted yet.'),
      observedAt: i.serverCheckedAt,
      ...(sv.deviceSubscribed === false && perm === 'granted' ? { action: 'subscribe-push' as const } : {}),
    });
  } else {
    checks.push({ id: 'provider', title: tr('Server och push-tjänst', 'Server and push service'), state: 'unknown', detail: tr('Ingen kontroll gjord ännu.', 'Not checked yet.'), observedAt: null, action: 'recheck' });
  }

  // 6. Enhetens rapport (service workern visade testnotisen)
  const shown = i.server?.lastDisplayedTest ?? null;
  checks.push({
    id: 'device',
    title: tr('Enhetens rapport', 'Device report'),
    state: !usesServer ? 'na' : shown ? 'ok' : 'unknown',
    detail: !usesServer
      ? tr('Gäller Web Push: webbläsaren rapporterar när en testnotis visas.', 'Applies to Web Push: the browser reports when a test notification is shown.')
      : shown
        ? tr(`Webbläsaren rapporterade att en testnotis ${shown.status === 'opened' ? 'öppnades' : 'visades'}.`, `The browser reported that a test notification was ${shown.status === 'opened' ? 'opened' : 'shown'}.`)
        : tr('Ingen rapport om visad testnotis.', 'No report of a shown test notification.'),
    observedAt: shown?.at ?? null,
  });

  // 7. Din bekräftelse
  const t = i.test;
  const sameChannel = !!t && t.channel === st.channel;
  const ageDays = t ? (Date.parse(i.now) - Date.parse(t.fireAt)) / 86_400_000 : Infinity;
  const permChanged = !!t?.permissionAtTest && t.permissionAtTest !== perm;
  let manual: GuardianCheck;
  if (!t) manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'attention', detail: tr('Inte verifierat med ett riktigt test. Använd "Testa om 1 minut".', 'Not verified with a real test. Use "Test in 1 minute".'), observedAt: null, action: 'run-test' };
  else if (t.result === 'not-seen') manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'risk', detail: tr('Du såg inte senaste testnotisen.', 'You did not see the latest test notification.'), observedAt: t.confirmedAt ?? t.fireAt, action: 'run-test' };
  else if (t.result === 'failed') manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'risk', detail: tr(`Testet kunde inte schemaläggas${t.detail ? `: ${t.detail}` : '.'}`, `The test could not be scheduled${t.detail ? `: ${t.detail}` : '.'}`), observedAt: t.scheduledAt, action: 'run-test' };
  else if (t.result === 'scheduled') manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'attention', detail: tr('Ett test väntar på ditt svar.', 'A test is waiting for your answer.'), observedAt: t.scheduledAt };
  else if (!sameChannel || permChanged) manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'attention', detail: tr('Senaste bekräftade test gjordes med en annan kanal eller behörighet. Testa igen.', 'The latest confirmed test was made with a different channel or permission. Test again.'), observedAt: t.confirmedAt ?? t.fireAt, action: 'run-test' };
  else if (ageDays > TEST_FRESH_DAYS) manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'attention', detail: tr(`Du bekräftade en testnotis för ${Math.floor(ageDays)} dagar sedan. Testa gärna igen.`, `You confirmed a test notification ${Math.floor(ageDays)} days ago. Consider testing again.`), observedAt: t.confirmedAt ?? t.fireAt, action: 'run-test' };
  else manual = { id: 'manual', title: tr('Din bekräftelse', 'Your confirmation'), state: 'ok', detail: tr('Du bekräftade att testnotisen syntes.', 'You confirmed that the test notification was shown.'), observedAt: t.confirmedAt ?? t.fireAt };
  checks.push(manual);

  const has = (s: CheckState) => checks.some((c) => c.state === s);
  // Föreslå bara det som kan lyckas nu: utan behörighet går varken push eller test att genomföra, och i
  // gästläget utan bakgrundskanal bevisar ett test ingenting – där är inloggning det viktiga steget.
  const blocked = perm !== 'granted' && st.channel !== 'none';
  const guestWeb = st.channel === 'none';
  const actions = [
    ...new Set(
      checks
        .filter((c) => c.action && (c.state === 'risk' || c.state === 'attention' || c.state === 'unknown'))
        .filter((c) => !(blocked && (c.action === 'run-test' || c.action === 'subscribe-push')))
        .filter((c) => !(guestWeb && c.action === 'run-test'))
        .sort((a, b) => rank(a.state) - rank(b.state) || (guestWeb ? Number(b.action === 'connect-account') - Number(a.action === 'connect-account') : 0))
        .map((c) => c.action!),
    ),
  ];
  let overall: GuardianResult['overall'];
  let headline: string;
  let summary: string;
  if (st.channel === 'none' && !has('risk')) {
    overall = 'limited';
    headline = tr('Påminnelser visas bara medan AutoDay är öppen', 'Reminders only show while AutoDay is open');
    summary = tr('Gästläget i webbläsaren har ingen bakgrundskanal. För påminnelser med stängd app: logga in och aktivera Web Push, eller använd Android-appen.', 'Guest mode in the browser has no background channel. For reminders with the app closed: sign in and turn on Web Push, or use the Android app.');
  } else if (has('risk')) {
    overall = 'risk';
    headline = tr('Påminnelser kan utebli på den här enheten', 'Reminders may not arrive on this device');
    summary = tr('Minst en sak som påminnelser behöver fungerar inte just nu. Se åtgärderna nedan.', 'At least one thing reminders depend on is not working right now. See the actions below.');
  } else if (has('attention') || has('unknown')) {
    overall = 'attention';
    headline = tr('Påminnelser är inställda men behöver kontrolleras', 'Reminders are set up but need checking');
    summary = tr('Inget är trasigt som AutoDay kan se, men allt är inte bekräftat.', 'Nothing AutoDay can see is broken, but not everything is confirmed.');
  } else {
    overall = 'ok';
    headline = tr('Påminnelser är inställda och testade på den här enheten', 'Reminders are set up and tested on this device');
    summary = tr('Allt som går att kontrollera härifrån är uppfyllt. Det är ingen garanti: en avstängd eller sovande enhet kan inte visa något.', 'Everything that can be checked from here is in place. It is not a guarantee: a switched-off or sleeping device cannot show anything.');
  }
  return { overall, headline, summary, checks, actions, lastCheckedAt, stale };
}

function rank(s: CheckState): number {
  return s === 'risk' ? 0 : s === 'attention' ? 1 : s === 'unknown' ? 2 : 3;
}
